import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useBrand } from '../../contexts/BrandContext';
import { supabase } from '../../utils/supabaseClient';
import { showToast } from '../Common/Toast';
import { US_STATES, normalizeStateToCode } from '@tradesmatepro/shared-ui';
import {
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  PhotoIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';


import { uploadCompanyAsset } from '../../utils/companyAssetUpload';
import { BUSINESS_TIMEZONE_GROUPS, detectBusinessTimezone } from '../../utils/timezones';
import PhoneField from '../Common/PhoneField';
import { normalizePhone } from '../../utils/phone';
// Native <select> popups are drawn by the OS, so they ignore the app's dark
// glassmorphism unless told: color-scheme dark themes the popup itself, and the
// option class themes each row. Without both, the wizard's dropdowns opened as a
// bright white list against a dark form. Same pair QuoteBuilder uses.
const selectOptionClass = 'bg-slate-800 text-gray-100';

const CompanyBasicsStep = ({ onComplete, onValidationChange, registerSave }) => {
  const { user } = useUser();
  const { brand } = useBrand();
  // PERSISTENCE FIX: Load from localStorage if available to handle component remounting
  const getInitialFormData = () => {
    try {
      const saved = localStorage.getItem(`onboarding_company_${user?.company_id}`);
      if (saved) {
        console.log('📦 Loading form data from localStorage');
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load form data from localStorage:', error);
    }

    return {
      name: '',
      company_logo_url: '',
      tagline: '',
      phone: '',
      email: '',
      website: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'United States',
      industry: '',
      industry_tags: [],
      timezone: 'America/Los_Angeles',
      profile_description: '' // Searchable description for niche businesses
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Which region control to show — see the note beside the field (board #613). The country
  // select offers 'United States' and 'Canada'; anything else stored still resolves sensibly.
  const isUSCompany = String(formData.country || 'United States').trim().toUpperCase() !== 'CANADA';

  const [loading, setLoading] = useState(true);
  // Set when the timezone was pre-selected from this device rather than chosen, so the field can say so.
  const [timezoneDetected, setTimezoneDetected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // #2848: messages wait until the user has edited a field or tried to continue. Validation
  // still runs from the start (the wizard needs `valid` to gate Continue); only the WORDS are
  // held back, so arriving at — or returning to — an untouched step is not a wall of red.
  const [revealErrors, setRevealErrors] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Business categories (broad, for marketplace filtering)
  // ONE taxonomy, ORDERED PER BRAND.
  //
  // The same 17 categories are offered on both brands and that is deliberate: a plumber
  // can sign up on SMBMate and an accountant on TradesMatePro, and the marketplace has to
  // match them from one pool. Two divergent lists would fragment matching.
  //
  // What differs is the ORDER. SMBMate opened this list with "Home Trades (Plumbing,
  // Electrical, HVAC, Roofing...)" as option one, so an SMB signup scrolled past five
  // trade categories to reach their own — the same wrong-first-impression class as the
  // toolbox artwork on the dashboard. Reordering is safe: nothing outside this screen
  // reads companies.industry (verified 2026-08-29 — its only consumers are this step's
  // tag suggestions and ServicesStep's template detection), so the order carries no
  // meaning beyond what a new signup sees first.
  //
  // Nothing is hidden from either brand, and 'other' stays last on both.
  const CATEGORY_LABELS = {
    home_trades: 'Home Trades (Plumbing, Electrical, HVAC, Roofing...)',
    home_services: 'Home Services (Cleaning, Landscaping, Pest Control...)',
    auto_fleet: 'Auto & Fleet Services',
    professional_services: 'Professional Services (Consulting, Accounting, Legal...)',
    health_wellness: 'Health & Wellness',
    beauty_personal: 'Beauty & Personal Care',
    food_beverage: 'Food & Beverage',
    retail_ecommerce: 'Retail & E-Commerce',
    tech_it: 'Tech & IT Services',
    creative_marketing: 'Creative & Marketing',
    education_tutoring: 'Education & Tutoring',
    fitness_recreation: 'Fitness & Recreation',
    pet_services: 'Pet Services',
    events_entertainment: 'Events & Entertainment',
    property_management: 'Property Management',
    construction: 'Construction & General Contracting',
    other: 'Other'
  };

  const CATEGORY_ORDER = {
    tradesmate: [
      'home_trades', 'home_services', 'construction', 'auto_fleet', 'property_management',
      'professional_services', 'retail_ecommerce', 'tech_it', 'health_wellness',
      'beauty_personal', 'food_beverage', 'creative_marketing', 'education_tutoring',
      'fitness_recreation', 'pet_services', 'events_entertainment', 'other'
    ],
    smbmate: [
      'professional_services', 'tech_it', 'health_wellness', 'beauty_personal',
      'retail_ecommerce', 'creative_marketing', 'education_tutoring', 'fitness_recreation',
      'food_beverage', 'pet_services', 'events_entertainment', 'home_services',
      'property_management', 'home_trades', 'construction', 'auto_fleet', 'other'
    ]
  };

  // Built from CATEGORY_LABELS rather than the order array, so a key missing from an
  // order list still renders (appended before 'other') instead of silently vanishing.
  const businessCategories = (() => {
    const order = CATEGORY_ORDER[brand?.code] || CATEGORY_ORDER.tradesmate;
    const ranked = [...Object.keys(CATEGORY_LABELS)].sort((a, b) => {
      if (a === 'other') return 1;
      if (b === 'other') return -1;
      const ia = order.indexOf(a); const ib = order.indexOf(b);
      return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
    });
    return [
      { value: '', label: 'Select a category...' },
      ...ranked.map(value => ({ value, label: CATEGORY_LABELS[value] }))
    ];
  })();

  // Service tag suggestions by category (fuzzy-matched as user types)
  const serviceTagsByCategory = {
    home_trades: [
      'Plumbing', 'Electrical', 'HVAC', 'Roofing', 'Flooring', 'Painting', 'Drywall',
      'Concrete', 'Masonry', 'Welding', 'Insulation', 'Waterproofing', 'Solar Installation',
      'Low Voltage', 'Fire Protection', 'Garage Doors', 'Window Installation', 'Door Installation',
      'Fencing', 'Siding', 'Tile & Stone', 'Septic Services', 'Excavation', 'Irrigation',
      'Faucet Repair', 'Water Heater', 'Tankless Water Heater', 'Drain Cleaning',
      'Panel Upgrade', 'EV Charger Install', 'Ceiling Fan Install', 'Outlet Repair',
      'AC Repair', 'Furnace Repair', 'Ductwork', 'Mini Split Install'
    ],
    home_services: [
      'House Cleaning', 'Deep Cleaning', 'Move-Out Cleaning', 'Landscaping', 'Lawn Care',
      'Tree Services', 'Pressure Washing', 'Pool Services', 'Pest Control', 'Handyman',
      'Appliance Repair', 'Locksmith', 'Moving Services', 'Junk Removal', 'Organizing',
      'Window Cleaning', 'Gutter Cleaning', 'Snow Removal', 'Holiday Lighting'
    ],
    auto_fleet: [
      'Oil Change', 'Brake Repair', 'Tire Service', 'Auto Detailing', 'Transmission',
      'Engine Repair', 'Fleet Maintenance', 'Mobile Mechanic', 'Towing', 'Windshield Repair',
      'Body Work', 'Paint Correction', 'Ceramic Coating'
    ],
    professional_services: [
      'Consulting', 'Accounting', 'Bookkeeping', 'Legal Services', 'Tax Preparation',
      'Financial Planning', 'Business Coaching', 'HR Services', 'Notary', 'Translation',
      'Virtual Assistant', 'Project Management'
    ],
    health_wellness: [
      'Dental', 'Chiropractic', 'Massage Therapy', 'Physical Therapy', 'Med Spa',
      'Counseling', 'Nutrition', 'Acupuncture', 'Home Health Care', 'Senior Care'
    ],
    beauty_personal: [
      'Hair Salon', 'Barbershop', 'Nail Salon', 'Skin Care', 'Makeup Artist',
      'Lash Extensions', 'Waxing', 'Tattoo', 'Piercing', 'Mobile Beauty'
    ],
    food_beverage: [
      'Catering', 'Food Truck', 'Bakery', 'Meal Prep', 'Private Chef',
      'Bar Service', 'Coffee Service', 'Restaurant'
    ],
    retail_ecommerce: [
      'Online Store', 'Retail Shop', 'Wholesale', 'Drop Shipping', 'Custom Orders',
      'Subscription Box', 'Marketplace Seller'
    ],
    tech_it: [
      'Computer Repair', 'Network Setup', 'Cybersecurity', 'Web Development',
      'App Development', 'IT Support', 'Cloud Services', 'Data Recovery',
      'Smart Home Setup', 'Security Cameras', 'Managed IT'
    ],
    creative_marketing: [
      'Graphic Design', 'Web Design', 'Photography', 'Videography', 'Social Media',
      'SEO', 'Content Writing', 'Branding', 'Print Design', 'Signage'
    ],
    education_tutoring: [
      'Math Tutoring', 'Science Tutoring', 'Language Lessons', 'Music Lessons',
      'Test Prep', 'Special Education', 'Online Courses', 'Driving School'
    ],
    fitness_recreation: [
      'Personal Training', 'Yoga', 'Pilates', 'Martial Arts', 'Swimming Lessons',
      'Sports Coaching', 'Dance', 'Outdoor Adventures'
    ],
    pet_services: [
      'Dog Walking', 'Pet Sitting', 'Dog Grooming', 'Pet Training', 'Veterinary',
      'Pet Boarding', 'Mobile Vet', 'Aquarium Service'
    ],
    events_entertainment: [
      'DJ Services', 'Event Planning', 'Photography', 'Videography', 'Florist',
      'Balloon Artist', 'Face Painting', 'Photo Booth', 'Live Music', 'Party Rentals'
    ],
    property_management: [
      'Property Management', 'Rental Management', 'HOA Management', 'Commercial Leasing',
      'Vacation Rental', 'Tenant Screening', 'Maintenance Coordination'
    ],
    construction: [
      'General Contracting', 'Remodeling', 'New Construction', 'Demolition',
      'Framing', 'Foundation', 'Commercial Build-Out', 'Restoration', 'ADU Construction'
    ],
    other: []
  };

  // All service tags flattened (for fuzzy search across categories)
  const allServiceTags = [...new Set(Object.values(serviceTagsByCategory).flat())].sort();

  const [serviceTagInput, setServiceTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  // Which suggestion the arrow keys are sitting on. -1 means "none — Enter adds what was
  // typed", which is how a custom service still gets in without ever touching the list.
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  /**
   * How well a tag matches what was typed. Lower is better; null means "do not show".
   *
   * The previous matcher was a plain ordered-subsequence test, which is far too generous on
   * short queries: "drain" matches **D**oo**r** Inst**a**llat**i**o**n** because d, r, a, i, n
   * all appear in that order. Reported after a manual run typed "drain" and was offered Door
   * Installation next to Drain Cleaning, with nothing to say why.
   *
   * Tiers are the standard typeahead ranking — exact, prefix, word-prefix, substring — so
   * "drain" puts Drain Cleaning first and never reaches for characters scattered across a
   * different word.
   */
  const MATCH_EXACT = 0;
  const MATCH_PREFIX = 1;
  const MATCH_WORD_PREFIX = 2;
  const MATCH_SUBSTRING = 3;
  const MATCH_FUZZY = 4;

  const scoreMatch = (text, query) => {
    const t = (text || '').toLowerCase();
    const q = (query || '').trim().toLowerCase().trim();
    if (!q) return null;

    if (t === q) return MATCH_EXACT;
    if (t.startsWith(q)) return MATCH_PREFIX;
    if (t.split(/[\s/&-]+/).some(word => word.startsWith(q))) return MATCH_WORD_PREFIX;
    if (t.includes(q)) return MATCH_SUBSTRING;

    // Subsequence fallback, kept DELIBERATELY tight and used only when nothing above matched
    // (see getTagSuggestions). Requiring the matched characters to sit close together is what
    // separates a typo from a coincidence: "drain" spans 16 characters of "Door Installation"
    // for a 5-character query, so it is rejected.
    if (q.length < 4) return null;
    let ti = 0;
    let first = -1;
    for (let qi = 0; qi < q.length; qi++) {
      while (ti < t.length && t[ti] !== q[qi]) ti++;
      if (ti >= t.length) return null;
      if (first === -1) first = ti;
      ti++;
    }
    const span = ti - first;
    return span <= q.length * 2 ? MATCH_FUZZY : null;
  };

  // Get relevant suggestions: category-specific first, then all
  const getTagSuggestions = () => {
    if (!serviceTagInput.trim()) {
      // Show category-specific tags when no input
      const categoryTags = serviceTagsByCategory[formData.industry] || [];
      return categoryTags.filter(tag => !(formData.industry_tags || []).includes(tag)).slice(0, 12);
    }
    // Ranked search across all tags.
    const currentTags = formData.industry_tags || [];
    const categoryTags = serviceTagsByCategory[formData.industry] || [];

    const scored = allServiceTags
      .filter(tag => !currentTags.includes(tag))
      .map(tag => ({ tag, score: scoreMatch(tag, serviceTagInput) }))
      .filter(m => m.score !== null);

    // The loose subsequence tier only gets a hearing when nothing solid matched at all.
    // Otherwise a real hit and a coincidence would sit in the same list, which is the
    // reported defect.
    const solid = scored.filter(m => m.score < MATCH_FUZZY);
    const pool = solid.length > 0 ? solid : scored;

    return pool
      .sort((a, b) => {
        // Textual relevance first — that is the whole point of the ranking.
        if (a.score !== b.score) return a.score - b.score;
        // The company's own category is a SECONDARY signal, not a primary one.
        const aInCat = categoryTags.includes(a.tag) ? 0 : 1;
        const bInCat = categoryTags.includes(b.tag) ? 0 : 1;
        if (aInCat !== bInCat) return aInCat - bInCat;
        return a.tag.localeCompare(b.tag);
      })
      .map(m => m.tag)
      .slice(0, 8);
  };

  const addTag = (tag) => {
    // Functional: the previous version read formData.industry_tags from the closure, which
    // is the half of the bug that made a rapid second tag overwrite the first.
    updateFormData(prev => {
      const current = prev.industry_tags || [];
      return current.includes(tag) ? prev : { ...prev, industry_tags: [...current, tag] };
    });
    setServiceTagInput('');
    setShowTagSuggestions(true); // Keep open for adding more
  };

  const removeTag = (tag) => {
    updateFormData(prev => ({
      ...prev,
      industry_tags: (prev.industry_tags || []).filter(t => t !== tag)
    }));
  };

  /**
   * KEYBOARD PATH THROUGH THE SUGGESTIONS.
   *
   * This handled Enter and Backspace only, with no notion of an active option — so typing
   * "drain" opened a list you could see and could not reach: ArrowDown did nothing, and Enter
   * added the raw typed text rather than the suggestion sitting right there. The list was
   * pointer-only, which also makes it unusable with a screen reader.
   *
   * Enter with nothing highlighted still adds the typed text, because a business that is not a
   * trade has to be able to type a service no curated tag covers.
   */
  const handleTagKeyDown = (e) => {
    const suggestions = showTagSuggestions ? getTagSuggestions() : [];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showTagSuggestions) {
        setShowTagSuggestions(true);
        setActiveSuggestion(0);
        return;
      }
      if (suggestions.length === 0) return;
      setActiveSuggestion(prev => (prev + 1) % suggestions.length);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length === 0) return;
      setActiveSuggestion(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (e.key === 'Escape') {
      // Close the list without losing what was typed — Escape here means "stop suggesting",
      // not "undo my work".
      setShowTagSuggestions(false);
      setActiveSuggestion(-1);
      return;
    }

    if (e.key === 'Enter') {
      const picked = activeSuggestion >= 0 ? suggestions[activeSuggestion] : null;
      if (picked) {
        e.preventDefault();
        addTag(picked);
        setActiveSuggestion(-1);
        return;
      }
      if (serviceTagInput.trim()) {
        e.preventDefault();
        addTag(serviceTagInput.trim());
        setActiveSuggestion(-1);
      }
      return;
    }

    if (e.key === 'Backspace' && !serviceTagInput && (formData.industry_tags || []).length > 0) {
      // Remove last tag on backspace in empty input
      const tags = formData.industry_tags || [];
      removeTag(tags[tags.length - 1]);
    }
  };

  // Resolve the selected service names into company_tags — the matchable junction
  // the marketplace matcher + Settings→Service Tags read. A curated tag is matched
  // by name (case-insensitive); anything the user typed that isn't curated becomes
  // a COMPANY-SCOPED custom tag (not every business fits a curated tag). Add-only
  // and idempotent — Settings manages removals.
  const syncCompanyTags = async (serviceNames) => {
    const names = [...new Set((serviceNames || []).map(n => (n || '').trim()).filter(Boolean))];
    if (names.length === 0) return;

    // Match against curated (company_id IS NULL) tags + this company's own customs.
    const { data: existing } = await supabase
      .from('tags')
      .select('id, name, company_id')
      .or(`company_id.is.null,company_id.eq.${user.company_id}`);
    const byLower = new Map();
    (existing || []).forEach(t => { if (t.name) byLower.set(t.name.toLowerCase(), t.id); });

    const tagIds = [];
    const errors = [];
    for (const name of names) {
      const hit = byLower.get(name.toLowerCase());
      if (hit) { tagIds.push(hit); continue; }
      // Create a company-scoped custom tag for the typed-in service.
      const { data: created, error: createErr } = await supabase
        .from('tags')
        .insert({ name, company_id: user.company_id, is_curated: false, tag_kind: 'service', category: 'CUSTOM' })
        .select('id')
        .single();
      if (createErr) { errors.push(`create "${name}": ${createErr.message}`); continue; }
      if (created) tagIds.push(created.id);
    }

    if (tagIds.length > 0) {
      const { error: upsertErr } = await supabase
        .from('company_tags')
        .upsert(
          tagIds.map(tag_id => ({ company_id: user.company_id, tag_id })),
          { onConflict: 'company_id,tag_id', ignoreDuplicates: true }
        );
      if (upsertErr) errors.push(`company_tags: ${upsertErr.message}`);
    }

    // Surface write failures (e.g. an RLS 403) instead of swallowing them — a silent
    // failure here is exactly what hid the company_tags RLS bug. The caller catches
    // this and pushes it to saveErrors.
    if (errors.length > 0) throw new Error(errors.join('; '));
  };

  // Load existing company data
  useEffect(() => {
    console.log('🔄 CompanyBasicsStep useEffect triggered');
    loadCompanyData();
  }, [user?.company_id]);

  // Debug: Log when component mounts/unmounts
  useEffect(() => {
    console.log('🎯 CompanyBasicsStep component mounted');
    return () => {
      console.log('🔚 CompanyBasicsStep component unmounting');
    };
  }, []);

  // Cleanup localStorage when component unmounts (optional)
  const clearFormCache = () => {
    try {
      localStorage.removeItem(`onboarding_company_${user?.company_id}`);
      console.log('🧹 Cleared form data cache');
    } catch (error) {
      console.warn('Failed to clear form data cache:', error);
    }
  };

  const loadCompanyData = async () => {
    if (!user?.company_id) return;

    try {
      setLoading(true);

      // INDUSTRY STANDARD: Pre-fill from existing company data (like Jobber/ServiceTitan)
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', user.company_id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        console.log('✅ Pre-filling company data from existing record');
        const prefilledData = {
          name: data.name || '',
          company_logo_url: data.company_logo_url || '',
          tagline: data.tagline || '',
          phone: data.phone || '',
          email: data.email || user.email || '', // Fallback to user email
          website: data.website || '',
          address_line1: data.address_line1 || data.street_address || '', // Handle both column names
          address_line2: data.address_line2 || '',
          city: data.city || '',
          state: data.state || data.state_province || '', // Handle both column names
          postal_code: data.postal_code || '',
          country: data.country || 'United States',
          industry: data.industry || '',
          industry_tags: data.industry_tags || [],
            timezone: data.timezone || data.time_zone || 'America/Los_Angeles', // Handle both column names
          profile_description: data.profile_description || '' // Searchable description
        };
        // A new company's timezone is the column default (Los Angeles), not a choice. On a first
        // visit — this step never saved, timezone still that default — start from the device's
        // own zone, as Jobber/HouseCall Pro do. A saved or later-changed choice is left alone.
        const { data: settingsRow } = await supabase
          .from('company_settings')
          .select('onboarding_progress')
          .eq('company_id', user.company_id)
          .maybeSingle();
        const chosenBefore = !!settingsRow?.onboarding_progress?.step_1_data?.timezone;
        const detected = !chosenBefore && prefilledData.timezone === 'America/Los_Angeles'
          ? detectBusinessTimezone(prefilledData.country)
          : null;
        if (detected) prefilledData.timezone = detected;
        setTimezoneDetected(!!detected);

        console.log('🔄 Setting form data from existing record');
        updateFormData(prefilledData);
      } else {
        // No existing company data - this shouldn't happen in normal flow
        console.log('⚠️ No existing company data found - using user email as fallback');
        const fallbackData = { ...formData, email: user.email || '' };
        updateFormData(fallbackData);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate form
  const validateForm = ({ reveal = revealErrors } = {}) => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Company name must be at least 2 characters';
    }

    if (formData.email && formData.email.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // BASIC INFO IS REQUIRED, the rest is not (operator ruling 2026-09-09). Company name,
    // a contact phone and the business category are what the product cannot work without:
    // the name goes on every quote and invoice, the phone is how a customer reaches the
    // contractor from those documents, and the category drives service recommendations and
    // marketplace matching. Address stays a warning -- useful, not load-bearing.
    //
    // These three were already the app's own "recommended" list, emitted as warnings that
    // nothing acted on, so this promotes an existing opinion rather than inventing a rule.
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required so customers can reach you';
    } else {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        newErrors.phone = 'Please enter a valid phone number (at least 10 digits)';
      }
    }

    if (!formData.industry) {
      newErrors.industry = 'Choose the category that best describes your business';
    }

    if (formData.website && formData.website.trim()) {
      const website = formData.website.trim();
      if (!website.match(/^https?:\/\/.+/) && !website.includes('.')) {
        newErrors.website = 'Please enter a valid website URL (e.g., yourcompany.com)';
      }
    }

    setErrors(reveal ? newErrors : {});
    
    // Update validation for parent component
    const isValid = Object.keys(newErrors).length === 0;
    const warnings = [];
    
    // Phone and industry are ERRORS now, not warnings — see validateForm above. Address
    // stays a warning: it improves the documents but nothing breaks without it.
    if (!formData.address_line1.trim()) {
      warnings.push('Business address recommended for professional appearance');
    }

    onValidationChange?.({
      valid: isValid,
      errors: reveal ? Object.values(newErrors) : [],
      warnings: reveal ? warnings : []
    });

    return isValid;
  };

  // PERSISTENCE FIX: Save to localStorage when form changes
  // Accepts an updater FUNCTION as well as a whole object.
  //
  // handleChange used to build `{ ...formData, [field]: value }` from the render closure,
  // so two updates that happened before a re-render both started from the SAME formData
  // and the second discarded the first. That is why adding a second service tag "worked
  // once, then stopped": addTag reads formData.industry_tags from the same closure, so the
  // second Enter appended to a stale array. A human typing slowly gets a re-render in
  // between and never notices; anything faster loses the first tag.
  //
  // Persistence moved OUT of here into an effect below. Writing localStorage inside a
  // state updater is a side effect in what must be a pure function -- React may invoke an
  // updater more than once.
  const updateFormData = (next) => {
    setFormData(prev => (typeof next === 'function' ? next(prev) : next));
  };

  // Handle form changes
  const handleChange = (field, value) => {
    setRevealErrors(true);
    updateFormData(prev => ({ ...prev, [field]: value }));
  };

  // Persist whatever the form currently holds. Guarded on `loading` so the initial
  // defaults cannot overwrite saved data before the load has populated state.
  useEffect(() => {
    if (loading || !user?.company_id) return;
    try {
      localStorage.setItem(`onboarding_company_${user.company_id}`, JSON.stringify(formData));
    } catch (error) {
      console.warn('Failed to save form data to localStorage:', error);
    }
  }, [formData, loading, user?.company_id]);

  // Validate on form changes (and when errors become revealable)
  useEffect(() => {
    if (!loading) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, loading, revealErrors]);

  // Save company data
  const handleSave = async () => {
    setRevealErrors(true);
    if (!validateForm({ reveal: true })) return;

    try {
      setSaving(true);

      // FIXED: Prepare update data (no id needed — using .eq filter)
      const companyData = {
        name: formData.name.trim(),
        updated_at: new Date().toISOString()
      };

      // Only add fields if they have values and meet constraints
      if (formData.email && formData.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        companyData.email = formData.email.trim();
      }

      // Board #2114. This re-derived E.164 by hand: it stripped every non-digit — which
      // throws away the "+" and therefore the country the user PICKED — then prepended
      // "1" to any 10-digit result, with the comment "Add US country code". PhoneField
      // above already emits E.164 from a ~240-country picker, so the country code was
      // chosen deliberately and then silently overwritten. It breaks for real countries,
      // not just in principle: a Norwegian (+47) or Danish (+45) number is 8 national
      // digits, so E.164 is exactly 10 digits and does not start with "1" — "+4712345678"
      // was stored as "+14712345678".
      //
      // normalizePhone (utils/phone.js) is the canonical owner of this conversion and
      // honours a leading "+" explicitly. Asking it is the whole fix.
      if (formData.phone && formData.phone.trim()) {
        const normalizedPhone = normalizePhone(formData.phone);
        if (normalizedPhone) {
          companyData.phone = normalizedPhone;
        }
      }

      if (formData.website && formData.website.trim()) {
        let website = formData.website.trim();
        if (!website.startsWith('http://') && !website.startsWith('https://')) {
          website = 'https://' + website;
        }
        // Only add if it looks like a valid URL
        if (website.includes('.')) {
          companyData.website = website;
        }
      }

      // Add other fields that don't have strict constraints (check if they exist in schema)
      // FIXED: Only include fields that exist in current schema
      const safeFields = {
        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        city: formData.city,
        state: formData.state,
        state_province: formData.state, // Fallback column name
        postal_code: formData.postal_code,
        country: formData.country,
        profile_description: formData.profile_description, // Searchable description for niche businesses
        industry: formData.industry,
        timezone: formData.timezone,
        tagline: formData.tagline
      };

      // Only add non-empty string fields
      Object.entries(safeFields).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          companyData[key] = value.toString().trim();
        }
      });

      // Add array fields (industry_tags for marketplace search)
      if (formData.industry_tags && formData.industry_tags.length > 0) {
        companyData.industry_tags = formData.industry_tags;
      }

      console.log('💾 Saving company data');

      // CRITICAL: Save in granular chunks to ensure ALL fields get saved, not just name
      // This fixes the bug where industry_tags wouldn't save if other fields failed
      
      let saveErrors = [];

      // Step 1: Save basic info (always required)
      const basicData = {
        name: formData.name.trim(),
        // Canonical logo column — companies.logo_url exists but is populated on 0 of 186
        // companies; company_logo_url is what the profile page and InvoiceTemplate read.
        company_logo_url: formData.company_logo_url || null,
        updated_at: new Date().toISOString()
      };
      
      let { error: basicError } = await supabase
        .from('companies')
        .update(basicData)
        .eq('id', user.company_id);
      
      if (basicError) {
        console.error('❌ Basic save failed:', basicError);
        saveErrors.push('basic: ' + basicError.message);
      }

      // Timezone ALSO has to land on company_settings — that is the row Business Settings
      // edits and the row the app reads to render every date (utils/dateFormatters.js).
      // Writing only `companies` meant the contractor picked a timezone here, then saw
      // 'America/New_York' in Settings forever and had all their dates rendered in it:
      // 60 of 63 companies were split-brained this way before the backfill migration.
      if (formData.timezone) {
        const { error: tzError } = await supabase
          .from('company_settings')
          .update({ timezone: formData.timezone })
          .eq('company_id', user.company_id);
        if (tzError) {
          console.error('❌ Timezone save to company_settings failed:', tzError);
          saveErrors.push('timezone: ' + tzError.message);
        }
      }

      // Step 2: Save contact info (email, phone, website)
      const contactData = {};
      if (formData.email && formData.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        contactData.email = formData.email.trim();
      }
      // Board #2114: the SECOND copy of the same hand-rolled US coercion, in the same
      // file. Both go through the canonical normalizePhone now.
      if (formData.phone && formData.phone.trim()) {
        const normalizedPhone = normalizePhone(formData.phone);
        if (normalizedPhone) contactData.phone = normalizedPhone;
      }
      if (formData.website && formData.website.trim()) {
        let website = formData.website.trim();
        if (!website.startsWith('http://') && !website.startsWith('https://')) {
          website = 'https://' + website;
        }
        if (website.includes('.')) contactData.website = website;
      }

      if (Object.keys(contactData).length > 0) {
        let { error: contactError } = await supabase
          .from('companies')
          .update(contactData)
          .eq('id', user.company_id);
        
        if (contactError) {
          console.warn('⚠️ Contact save failed:', contactError);
          saveErrors.push('contact: ' + contactError.message);
        }
      }

      // Step 3: Save address info
      // NOTE: the live `companies` table uses `state_province` (there is no `state` column),
      // so the form's `state` value must be written to `state_province` or the PATCH 400s and
      // the whole address chunk fails to persist.
      const addressData = {};
      const addressFields = ['address_line1', 'address_line2', 'city', 'postal_code', 'country'];
      addressFields.forEach(field => {
        if (formData[field] && formData[field].toString().trim()) {
          addressData[field] = formData[field].toString().trim();
        }
      });
      if (formData.state && formData.state.toString().trim()) {
        addressData.state_province = formData.state.toString().trim();
      }

      if (Object.keys(addressData).length > 0) {
        let { error: addressError } = await supabase
          .from('companies')
          .update(addressData)
          .eq('id', user.company_id);
        
        if (addressError) {
          console.warn('⚠️ Address save failed:', addressError);
          saveErrors.push('address: ' + addressError.message);
        }
      }

      // Step 4: Save business profile (industry, tags, description) - THIS WAS THE BUG
      const profileData = {};
      if (formData.industry && formData.industry.trim()) {
        profileData.industry = formData.industry.trim();
      }
      // When "Other" is chosen, persist the user's free-text via industry_tags
      // (companies has no dedicated industry_other column).
      let industryTags = formData.industry_tags || [];
      if (
        formData.industry === 'other' &&
        formData.industry_other &&
        formData.industry_other.trim() &&
        !industryTags.includes(formData.industry_other.trim())
      ) {
        industryTags = [...industryTags, formData.industry_other.trim()];
      }
      if (industryTags.length > 0) {
        profileData.industry_tags = industryTags;
      }
      if (formData.profile_description && formData.profile_description.trim()) {
        profileData.profile_description = formData.profile_description.trim();
      }
      if (formData.timezone) {
        profileData.timezone = formData.timezone;
      }
      if (formData.tagline && formData.tagline.trim()) {
        profileData.tagline = formData.tagline.trim();
      }

      if (Object.keys(profileData).length > 0) {
        let { error: profileError } = await supabase
          .from('companies')
          .update(profileData)
          .eq('id', user.company_id);
        
        if (profileError) {
          console.error('❌ Profile save failed (industry/tags):', profileError);
          saveErrors.push('profile: ' + profileError.message);
        } else {
          console.log('✅ Profile data (industry/tags) saved successfully');
        }
      }

      // Sync selected services into company_tags so they actually reach the
      // marketplace matcher (industry_tags alone never did). Non-fatal.
      if (industryTags.length > 0) {
        try {
          await syncCompanyTags(industryTags);
          console.log('✅ Company tags synced');
        } catch (tagErr) {
          console.warn('⚠️ Company tag sync failed:', tagErr.message);
          saveErrors.push('tags: ' + tagErr.message);
        }
      }

      // If ALL saves failed, throw error
      if (saveErrors.length > 0 && saveErrors.length >= 3) {
        throw new Error('Failed to save company data: ' + saveErrors.join(', '));
      }

      // Warn but continue if some saves failed
      if (saveErrors.length > 0) {
        console.warn('⚠️ Some fields failed to save:', saveErrors);
        showToast('warning', 'Some fields may not have saved. You can update them later in Settings.');
      }

      // Settings are now canonical in the settings table; onboarding no longer updates legacy company_settings

      console.log('✅ Company data saved successfully');

      // INDUSTRY STANDARD: Always proceed to next step even if some saves fail
      // This prevents users from getting stuck in onboarding
      onComplete?.(formData);
    } catch (error) {
      console.error('❌ Error saving company data:', error);
      setErrors({ general: `Failed to save company information: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  // F-ATLAS-0812-1609-STEP1-NO-PATCH: let the wizard's footer "Continue" save this step
  // too, not just the in-form "Save & Continue" button. OnboardingWizard.goNext() runs the
  // registered save if there is one and otherwise calls handleStepComplete() directly,
  // which ADVANCES WITHOUT SAVING — so footer-Continue produced 0 PATCHes to `companies`
  // and every optional Step 1 field (industry / website / address / description /
  // timezone / services) stayed NULL on the new company. The July fix 3c59be81 added this
  // to Steps 4 and 5 only and missed Step 1.
  //
  // Keep `handleSave` UNMEMOIZED and in the dep array, matching
  // BusinessPreferencesStep.js:182. Do NOT "optimise" it with useCallback: the wizard
  // clears stepSaveRef on every step change (OnboardingWizard.js:428) and React runs child
  // effects BEFORE parent effects, so a stable callback would register once and then be
  // nulled by the parent. A new identity each render re-registers on the following render,
  // which is what makes the existing two steps work.
  useEffect(() => { registerSave?.(handleSave); }, [registerSave, handleSave]);

  if (loading) {
    return (
      <div className="text-center py-12 [color-scheme:dark]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-slate-400">Loading company information...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <BuildingOfficeIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        {/* Heading removed: the wizard header (OnboardingWizard) already renders the step
            name, so an in-step <h2>Company Basics</h2> here was a duplicate. */}
        <p className="text-slate-400">
          Let's start with your basic company information. This will appear on quotes, invoices, and your customer portal.
        </p>
      </div>

      {errors.general && (
        <div className="bg-red-900/30 border border-red-700/40 rounded-md p-4 mb-6">
          <p className="text-red-300">{errors.general}</p>
        </div>
      )}

      {/* INDUSTRY STANDARD: Show pre-filled data notice */}
      {(formData.name || formData.email) && (
        <div className="bg-blue-900/30 border border-blue-700/40 rounded-md p-4 mb-6">
          <div className="flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
            <p className="text-blue-300">
              <strong>Great!</strong> We've pre-filled some information from your account.
              Please review and add any missing details below.
            </p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 space-y-6">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Company Name *
          </label>
          {/* companies.name is unbounded `text`, so nothing stopped a 200-character name
              reaching the column -- and one did: the longest live name is exactly 200
              against an average of 26. maxLength is a real limit here rather than
              decoration, because the name is rendered into invoice and quote PDFs and a
              200-char string does not lay out. 120 leaves generous room for a long legal
              name with a suffix. */}
          <input
            type="text"
            maxLength={120}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg bg-slate-800/60 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.name ? 'border-red-300' : 'border-slate-700/40'
            }`}
            placeholder="Your Company Name"
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Company Logo — step 1 runs in BOTH the quick and the full wizard, so this is
            the only place a logo can be collected once for everyone. Without it every
            quote, PDF and invoice a new contractor sends goes out unbranded, which is the
            first thing their customer sees. Uses the shared uploader so there is one
            bucket and one path convention, not a second copy. */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Company Logo <span className="text-slate-500 font-normal">(optional)</span>
          </label>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg border border-slate-700/40 bg-slate-800/60 flex items-center justify-center overflow-hidden shrink-0">
              {formData.company_logo_url ? (
                <img src={formData.company_logo_url} alt="Company logo" className="w-full h-full object-contain" />
              ) : (
                <PhotoIcon className="w-7 h-7 text-slate-500" />
              )}
            </div>
            <div className="flex items-center gap-3">
              <label className="px-3 py-2 text-sm rounded-lg border border-slate-700/40 bg-slate-800/60 text-white cursor-pointer hover:bg-slate-700/60">
                {logoUploading ? 'Uploading…' : formData.company_logo_url ? 'Replace' : 'Upload logo'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={logoUploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    try {
                      setLogoUploading(true);
                      const url = await uploadCompanyAsset(file, user?.company_id, 'company_logo');
                      if (url) handleChange('company_logo_url', url);
                    } catch (err) {
                      showToast('error', err?.message || 'Logo upload failed');
                    } finally {
                      setLogoUploading(false);
                    }
                  }}
                />
              </label>
              {formData.company_logo_url && (
                <button
                  type="button"
                  onClick={() => handleChange('company_logo_url', '')}
                  className="text-sm text-slate-400 hover:text-red-400"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <p className="mt-1 text-xs text-slate-500">Shown on your quotes, invoices and customer portal. PNG or JPG, up to 5MB.</p>
        </div>

        {/* Tagline */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Tagline (Optional)
          </label>
          <input
            type="text"
            value={formData.tagline}
            onChange={(e) => handleChange('tagline', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Your professional tagline"
          />
        </div>

        {/* Business Category */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Business Category *
          </label>
          <select
            value={formData.industry}
            onChange={(e) => {
              handleChange('industry', e.target.value);
              // Clear tags when switching category (optional UX choice)
            }}
            className="w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
          >
            {businessCategories.map(cat => (
              <option key={cat.value} value={cat.value} className="bg-slate-800 text-white">{cat.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-600 dark:text-slate-500">Helps customers find you on the marketplace</p>
          {/* The category error belongs to the category field. It was rendered four fields
              lower, INSIDE the address grid between the State select and the ZIP input, so
              "Choose the category that best describes your business" appeared over the ZIP
              box -- and because it was a direct child of a 6-column grid it also took a
              column, pushing ZIP across and wrapping Country onto its own row. The message
              named a field nowhere near the thing it was pointing at. */}
          {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
          {/* Free-text fallback when "Other" is selected so niche businesses
              aren't forced into a preset category. */}
          {formData.industry === 'other' && (
            <input
              type="text"
              value={formData.industry_other || ''}
              onChange={(e) => handleChange('industry_other', e.target.value)}
              className="w-full mt-2 px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Tell us what kind of business you run"
            />
          )}
        </div>

        {/* Services You Offer (Tags) */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Services You Offer
          </label>
          <div className="w-full min-h-[42px] px-2 py-1.5 border rounded-lg bg-slate-800/60 border-slate-700/40 focus-within:ring-2 focus-within:ring-blue-500 flex flex-wrap gap-1.5 items-center">
            {(formData.industry_tags || []).map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600/30 text-blue-300 text-sm rounded-full border border-blue-500/30"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-red-400 transition-colors ml-0.5"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              value={serviceTagInput}
              // #4271: a typed service becomes a `tags` row (syncCompanyTags), so it gets the same
              // 50-character ceiling as Settings → Service Tags (TagSelector, board #2194). This
              // input had none — a 76-character tag was created here on 2026-09-22.
              maxLength={50}
              onChange={(e) => {
                setServiceTagInput(e.target.value);
                setShowTagSuggestions(true);
                // The list is rebuilt on every keystroke, so an index into the old one is
                // meaningless — highlighting whatever happens to land in that slot would be
                // worse than highlighting nothing.
                setActiveSuggestion(-1);
              }}
              onFocus={() => setShowTagSuggestions(true)}
              onBlur={() => setTimeout(() => { setShowTagSuggestions(false); setActiveSuggestion(-1); }, 200)}
              onKeyDown={handleTagKeyDown}
              role="combobox"
              aria-expanded={showTagSuggestions && getTagSuggestions().length > 0}
              aria-controls="service-tag-suggestions"
              aria-autocomplete="list"
              aria-activedescendant={
                activeSuggestion >= 0 ? `service-tag-option-${activeSuggestion}` : undefined
              }
              className="flex-1 min-w-[120px] bg-transparent text-white placeholder-slate-500 outline-none text-sm py-1"
              placeholder={(formData.industry_tags || []).length === 0 ? "Type to search services (e.g. Faucet Repair, AC Install...)" : "Add more..."}
            />
          </div>
          {showTagSuggestions && getTagSuggestions().length > 0 && (
            <div
              id="service-tag-suggestions"
              role="listbox"
              className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-slate-800 border border-slate-600 rounded-lg shadow-xl"
            >
              {getTagSuggestions().map((tag, index) => (
                <button
                  key={tag}
                  id={`service-tag-option-${index}`}
                  role="option"
                  aria-selected={index === activeSuggestion}
                  type="button"
                  // The arrow-key highlight has to be a real background, not :hover — a
                  // keyboard user never generates a hover, so without this the active option
                  // is invisible and ArrowDown looks like it did nothing.
                  className={`w-full text-left px-3 py-2 text-sm text-slate-200 transition-colors ${
                    index === activeSuggestion ? 'bg-slate-700' : 'hover:bg-slate-700'
                  }`}
                  // onMouseDown fires BEFORE the input's blur, which is what stops the
                  // dropdown closing out from under the pointer -- that part is deliberate
                  // and stays. But mousedown alone meant the buttons did nothing for a
                  // keyboard user pressing Enter or Space, which dispatch click and never
                  // mousedown. (It is also why an automated .click() reported these as
                  // inert: HTMLElement.click() fires click only.)
                  //
                  // addTag already ignores a tag that is present, so a pointer firing both
                  // handlers adds it once.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addTag(tag);
                  }}
                  onClick={() => addTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-600 dark:text-slate-500">
            Type to search or add custom services • Press Enter to add • These become your marketplace tags
          </p>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* The same control Customers and Signup use. This was a bare text box, so the
              value normalised at signup came back to be edited as a raw "+15098647357" —
              a number the contractor typed, shown back in a shape nobody types. PhoneField
              splits the calling code into its own picker and renders the rest readably. */}
          <div>
            <PhoneField
              label={<><PhoneIcon className="w-4 h-4 inline mr-1" />Phone Number *</>}
              ariaLabel="Phone number"
              value={formData.phone}
              onChange={(phone) => handleChange('phone', phone)}
              error={errors.phone}
              testId="onboarding-company-phone"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <EnvelopeIcon className="w-4 h-4 inline mr-1" />
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-slate-800/60 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-300' : 'border-slate-700/40'
              }`}
              placeholder="info@yourcompany.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>
        </div>

        {/* Website */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <GlobeAltIcon className="w-4 h-4 inline mr-1" />
            Website (Optional)
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://yourcompany.com"
          />
          {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <MapPinIcon className="w-4 h-4 inline mr-1" />
            Business Address
          </label>
          <div className="space-y-3">
            <input
              type="text"
              value={formData.address_line1}
              onChange={(e) => handleChange('address_line1', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Street Address"
            />
            <input
              type="text"
              value={formData.address_line2}
              onChange={(e) => handleChange('address_line2', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Apartment, suite, etc. (optional)"
            />
            {/* 6 columns, not 4 equal ones: "OR — Oregon" needs roughly double the
                width of a ZIP field, and equal columns truncated it mid-word. */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="md:col-span-2 w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="City"
              />
              {/* Board #613. This always rendered US_STATES, whatever Country said, so
                  selecting Canada left a list of US states with CA still selected — and CA
                  reads as California here and as Canada one field below. The region control
                  has to follow the country, and the previously chosen region has to go with
                  it: keeping "CA" after switching country is how a Canadian company ends up
                  filed in California. */}
              {isUSCompany ? (
              <select
                value={normalizeStateToCode(formData.state)}
                onChange={(e) => handleChange('state', e.target.value)}
                className="md:col-span-2 w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
              >
                <option className={selectOptionClass} value="">State</option>
                {US_STATES.map(s => (
                  <option className={selectOptionClass} key={s.code} value={s.code}>{s.code} — {s.name}</option>
                ))}
              </select>
              ) : (
              <input
                type="text"
                value={formData.state || ''}
                onChange={(e) => handleChange('state', e.target.value)}
                className="md:col-span-2 w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Province / Region"
              />
              )}
              {/* Two columns, not one. Four fields shared six columns, which left the postal
                  code 1/6 of the row minus 24px of horizontal padding — enough for "97058-43"
                  and not the "97058-4321" that was actually stored. The value was never lost,
                  only unreadable, which is the worse failure: it looks like the field truncated
                  your input. City and State keep their existing 2/6 each. */}
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                className="md:col-span-2 w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                // Canada is an offered country one field along, and "ZIP Code" is not what a
                // Canadian contractor is looking for.
                placeholder="ZIP / Postal code"
              />
            </div>
            {/* Country gets its own row rather than a sixth of one. It is the field that made
                the address line overcrowded, and "United States" does not fit in 1/6 either. */}
            <select
              value={formData.country}
              onChange={(e) => {
                // Clear the region when the country changes — see the note on the field above.
                if (e.target.value !== formData.country) handleChange('state', '');
                handleChange('country', e.target.value);
              }}
              className="mt-3 w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            >
              <option className={selectOptionClass} value="United States">United States</option>
              <option className={selectOptionClass} value="Canada">Canada</option>
            </select>
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Timezone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => { handleChange('timezone', e.target.value); setTimezoneDetected(false); }}
            className="w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
          >
            {/* From utils/timezones.js, which this screen and Business Settings now share.
                Both used to carry their own seven-US-zone copy — this one even had a comment
                pointing at the other as "canonical" — so a contractor who picked Canada as
                their country one field earlier had no timezone describing where they work. */}
            {BUSINESS_TIMEZONE_GROUPS.map(group => (
              <optgroup key={group.country} label={group.country}>
                {group.zones.map(zone => (
                  <option className={selectOptionClass} key={zone.value} value={zone.value}>
                    {zone.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {timezoneDetected && (
            <p className="mt-1 text-xs text-gray-600 dark:text-slate-500">
              Set from this device. Change it if your business runs on a different time zone — every job time, invoice date and reminder uses it.
            </p>
          )}
        </div>

        {/* Company Description - Searchable for niche businesses */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <SparklesIcon className="w-4 h-4 inline mr-1" />
            What kind of work do you take? *
          </label>
          <textarea
            value={formData.profile_description}
            onChange={(e) => handleChange('profile_description', e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg bg-slate-800/60 border-slate-700/40 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
              formData.profile_description.length < 30 ? 'border-amber-300' : 'border-green-300'
            }`}
            placeholder="Example: We specialize in low-voltage controls, industrial automation, and custom panel fabrication. We also handle emergency electrical repairs and new installations."
          />
          <div className="mt-2 flex items-center justify-between text-sm">
            <p className="text-gray-600 dark:text-slate-400">
              {formData.profile_description.length} characters
              {formData.profile_description.length < 30 && (
                <span className="ml-2 text-amber-600">(30+ recommended)</span>
              )}
            </p>
            {formData.profile_description.length >= 30 && (
              <span className="text-green-600 font-medium">✓ 30+ characters</span>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-600 dark:text-slate-500">
            Shown on your public business page, and helps customers searching the marketplace find you even if your work doesn't fit neatly into categories.
          </p>
        </div>
      </div>

      {/* NO SECOND PRIMARY BUTTON HERE. The wizard footer already shows Continue (or Complete Setup on the final step), and it runs THIS step's save -- the same handler this button called, registered via registerSave. Two primary actions on one screen, worded differently, asked the user to choose between them when there was no choice to make. */}
    </div>
  );
};

export default CompanyBasicsStep;


